import { computeAnalyticsEcommerceItemsTotalValue, convertProductsToAnalyticsEcommerceItems } from './analyticsProduct';
import pushGTMEvent, {
  EcommerceAddPaymentInfoDataLayerEvent,
  EcommerceBeginCheckoutDataLayerEvent,
  EcommerceEvent,
  EcommerceViewCartDataLayerEvent,
  pushGTMEcommerceEvent,
} from '@helpers/analyticsHelpers/pushGTMEvent';
import { PaymentMode } from '@molecules/PaymentModes/PaymentModes';
import { CheckoutState } from '@slices/checkoutSlice';
import type { AxfoodCartViewModel } from '@occ/api-client';

export const trackViewCart = (
  cart?: AxfoodCartViewModel,
  parameters?: Omit<
    EcommerceViewCartDataLayerEvent['ecommerce'],
    'event' | 'source' | 'currency' | 'items' | 'value' | 'cart_id'
  >,
  isLoggedIn?: boolean
) => {
  if (!cart || cart.products.length === 0) {
    pushGTMEvent({
      event: 'Track',
      category: 'error_page',
      action: 'cart_empty',
      label: '',
      value: 0,
    });
  } else {
    const items = convertProductsToAnalyticsEcommerceItems(cart.products, undefined, undefined, isLoggedIn);
    pushGTMEcommerceEvent({
      event: EcommerceEvent.VIEW_CART,
      ecommerce: {
        ...parameters,
        cart_id: cart.code,
        items,
        value: computeAnalyticsEcommerceItemsTotalValue(items),
      },
    });
  }
};

export const trackBeginCheckout = (
  cart: AxfoodCartViewModel,
  parameters?: Omit<
    EcommerceBeginCheckoutDataLayerEvent['ecommerce'],
    'event' | 'source' | 'currency' | 'items' | 'value'
  >,
  isLoggedIn?: boolean
) => {
  const items = convertProductsToAnalyticsEcommerceItems(cart.products, undefined, undefined, isLoggedIn);
  pushGTMEcommerceEvent({
    event: EcommerceEvent.BEGIN_CHECKOUT,
    ecommerce: {
      ...parameters,
      items,
      value: computeAnalyticsEcommerceItemsTotalValue(items),
    },
  });
};

export const trackAddPaymentInfo = (
  cart: AxfoodCartViewModel,
  payment: CheckoutState['payment'],
  parameters?: Omit<
    EcommerceAddPaymentInfoDataLayerEvent['ecommerce'],
    'event' | 'source' | 'currency' | 'items' | 'value' | 'payment_mode'
  >,
  isLoggedIn?: boolean
) => {
  let payment_method: EcommerceAddPaymentInfoDataLayerEvent['ecommerce']['payment_method'] | undefined = undefined;
  switch (payment.selectedMode) {
    case PaymentMode.INVOICE:
      payment_method = 'invoice';
      break;
    case PaymentMode.PAYEX:
    case PaymentMode.PSPPAYEXALL:
    case PaymentMode.PAYEXALL:
      if (payment.selectedCard?.id) {
        payment_method = payment.selectedCard.id === 'new' ? 'new_card' : 'existing_card';
      }
      break;
    case PaymentMode.KLARNA:
      payment_method = 'Klarna';
      break;
    default:
      break;
  }
  if (payment_method) {
    const items = convertProductsToAnalyticsEcommerceItems(cart.products, undefined, undefined, isLoggedIn);
    pushGTMEcommerceEvent({
      event: EcommerceEvent.ADD_PAYMENT_INFO,
      ecommerce: {
        ...parameters,
        items,
        value: computeAnalyticsEcommerceItemsTotalValue(items),
        payment_method,
      },
    });
  }
};
